import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationItem } from '@globalbridge/types';
import { NotifDropdown } from '../v2/ds';
import {
  useMarkNotificationsRead,
  useNotificationsBadge,
  useNotificationsList,
} from '../../hooks/api/useNotifications';

/**
 * Top-bar bell (mock notifications-mock `.bell` + `.dd`). The badge is the
 * Needs-you count (Ari 2026-10-08), from `notifications.badge`. Opening it
 * shows Needs you (3, then "N more") and the latest 3 updates. Shown only to
 * v2-preview users (the navbars gate it).
 */
const NotificationsBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const badge = useNotificationsBadge();
  // The list is read only while the panel is open; the badge is its own small read.
  const list = useNotificationsList({}, { enabled: open });
  const read = useMarkNotificationsRead();
  const needsYou = badge.data?.needsYou ?? 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const data = list.data;
  const impersonating = data?.viewer.impersonating ?? false;
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  const onOpen = (item: NotificationItem) => {
    if (!impersonating && item.unread) read.markIds([item.id]);
    go(item.link);
  };
  const latest = data ? [...data.updates, ...data.done].sort((a, b) => b.sourceAt.localeCompare(a.sourceAt)) : [];

  return (
    <div className="ds-n-bellwrap" ref={wrap}>
      <button
        type="button"
        className="ds-n-bell"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={needsYou > 0 ? `Notifications (${needsYou} need you)` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 01-3.4 0" />
        </svg>
        {needsYou > 0 && <span className="ds-n-badge num" data-overhang="badge">{needsYou > 9 ? '9+' : needsYou}</span>}
      </button>
      {open && (
        <NotifDropdown
          needs={data?.needs ?? []}
          latest={latest}
          needsYou={data?.needsYou ?? needsYou}
          unread={data?.unread ?? badge.data?.unread ?? 0}
          onOpen={onOpen}
          onMarkAll={impersonating || !data ? undefined : read.markAll}
          onSeeAll={() => go('/notifications')}
          onEmail={() => go('/notifications#email')}
        />
      )}
    </div>
  );
};

export default NotificationsBell;
